import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Briefcase, Search, Plus, Filter, Calendar, User,
  Building, MapPin, FileText, Clock, Tag, ChevronRight,
  X, Check, Loader2, AlertCircle
} from 'lucide-react';

interface Matter {
  id: string;
  number: string;
  name: string;
  clientId: string;
  clientName: string;
  clientType: 'individual' | 'entity';
  type: string;
  status: 'intake' | 'active' | 'pending_client' | 'pending_third_party' | 'on_hold' | 'closing' | 'closed';
  priority: 'low' | 'normal' | 'high' | 'urgent';
  responsibleAttorney: string;
  openedDate: Date;
  documentCount: number;
  property?: {
    address: string;
    city: string;
    county: string;
  };
}

const MATTER_TYPES = [
  { value: 'real_estate_purchase', label: 'Real Estate Purchase' },
  { value: 'real_estate_sale', label: 'Real Estate Sale' },
  { value: 'real_estate_refinance', label: 'Real Estate Refinance' },
  { value: 'estate_planning_will', label: 'Estate Planning - Will' },
  { value: 'estate_planning_trust', label: 'Estate Planning - Trust' },
  { value: 'business_formation', label: 'Business Formation' },
  { value: 'business_transaction', label: 'Business Transaction' },
  { value: 'probate', label: 'Probate' },
  { value: 'trust_administration', label: 'Trust Administration' },
  { value: 'other', label: 'Other' },
];

const STATUS_OPTIONS = [
  { value: 'intake', label: 'Intake', color: 'blue' },
  { value: 'active', label: 'Active', color: 'green' },
  { value: 'pending_client', label: 'Pending Client', color: 'yellow' },
  { value: 'pending_third_party', label: 'Pending Third Party', color: 'orange' },
  { value: 'on_hold', label: 'On Hold', color: 'gray' },
  { value: 'closing', label: 'Closing', color: 'purple' },
  { value: 'closed', label: 'Closed', color: 'gray' },
];

export function MatterList() {
  const [matters, setMatters] = useState<Matter[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [showNewMatterModal, setShowNewMatterModal] = useState(false);
  const [selectedMatter, setSelectedMatter] = useState<Matter | null>(null);

  useEffect(() => {
    loadMatters();
  }, []);

  const loadMatters = async () => {
    setLoading(true);
    await new Promise(resolve => setTimeout(resolve, 500));

    setMatters([
      {
        id: '1',
        number: '2024-0089-REP',
        name: 'Smith Property Purchase',
        clientId: '1',
        clientName: 'John Smith',
        clientType: 'individual',
        type: 'real_estate_purchase',
        status: 'active',
        priority: 'high',
        responsibleAttorney: 'Sarah Johnson',
        openedDate: new Date('2024-10-15'),
        documentCount: 8,
        property: {
          address: '123 Main Street',
          city: 'Sturgeon Bay',
          county: 'Door',
        },
      },
      {
        id: '2',
        number: '2024-0085-EPT',
        name: 'Johnson Family Trust',
        clientId: '4',
        clientName: 'Johnson Family Trust',
        clientType: 'entity',
        type: 'estate_planning_trust',
        status: 'pending_client',
        priority: 'normal',
        responsibleAttorney: 'Sarah Johnson',
        openedDate: new Date('2024-09-20'),
        documentCount: 12,
      },
      {
        id: '3',
        number: '2024-0092-BUF',
        name: 'ABC Holdings LLC Formation',
        clientId: '2',
        clientName: 'ABC Holdings LLC',
        clientType: 'entity',
        type: 'business_formation',
        status: 'closing',
        priority: 'normal',
        responsibleAttorney: 'Sarah Johnson',
        openedDate: new Date('2024-11-01'),
        documentCount: 6,
      },
      {
        id: '4',
        number: '2024-0078-RES',
        name: 'Doe Property Sale',
        clientId: '3',
        clientName: 'Jane Doe',
        clientType: 'individual',
        type: 'real_estate_sale',
        status: 'closed',
        priority: 'low',
        responsibleAttorney: 'Sarah Johnson',
        openedDate: new Date('2024-08-05'),
        documentCount: 15,
        property: {
          address: '456 Oak Avenue',
          city: 'Green Bay',
          county: 'Brown',
        },
      },
    ]);
    setLoading(false);
  };

  const filteredMatters = matters.filter(matter => {
    const matchesSearch = 
      matter.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      matter.number.toLowerCase().includes(searchQuery.toLowerCase()) ||
      matter.clientName.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || matter.status === statusFilter;
    const matchesType = typeFilter === 'all' || matter.type === typeFilter;
    return matchesSearch && matchesStatus && matchesType;
  });

  const getStatusColor = (status: Matter['status']) => {
    const option = STATUS_OPTIONS.find(s => s.value === status);
    const colors: Record<string, string> = {
      blue: 'bg-blue-500/20 text-blue-400',
      green: 'bg-green-500/20 text-green-400',
      yellow: 'bg-yellow-500/20 text-yellow-400',
      orange: 'bg-orange-500/20 text-orange-400',
      gray: 'bg-gray-500/20 text-gray-400',
      purple: 'bg-purple-500/20 text-purple-400',
    };
    return colors[option?.color || 'gray'];
  };

  const getPriorityColor = (priority: Matter['priority']) => {
    const colors: Record<string, string> = {
      low: 'text-gray-400',
      normal: 'text-blue-400',
      high: 'text-orange-400',
      urgent: 'text-red-400',
    };
    return colors[priority];
  };

  const getTypeLabel = (type: string) => {
    return MATTER_TYPES.find(t => t.value === type)?.label || type;
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Briefcase className="w-7 h-7 text-accent-400" />
            Matters
          </h1>
          <p className="text-primary-400 mt-1">
            {matters.filter(m => m.status === 'active').length} active matters
          </p>
        </div>
        
        <button
          onClick={() => setShowNewMatterModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-accent-500 text-white rounded-lg hover:bg-accent-600 transition-colors"
        >
          <Plus className="w-5 h-5" />
          New Matter
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 mb-6">
        <div className="relative flex-1 min-w-[250px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-primary-400" />
          <input
            type="text"
            placeholder="Search matters..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-primary-800 border border-primary-600 text-white rounded-lg focus:outline-none focus:border-accent-500"
          />
        </div>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 bg-primary-800 border border-primary-600 text-white rounded-lg"
        >
          <option value="all">All Status</option>
          {STATUS_OPTIONS.map(s => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>

        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="px-4 py-2 bg-primary-800 border border-primary-600 text-white rounded-lg"
        >
          <option value="all">All Types</option>
          {MATTER_TYPES.map(t => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </select>
      </div>

      {/* Matter List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 text-accent-400 animate-spin" />
        </div>
      ) : filteredMatters.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-64 text-primary-400">
          <Briefcase className="w-16 h-16 mb-4 opacity-30" />
          <p className="text-lg">No matters found</p>
          <p className="text-sm mt-1">Try adjusting your filters</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {filteredMatters.map((matter, idx) => (
            <motion.div
              key={matter.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.05 }}
              className="bg-primary-800/50 border border-primary-700 rounded-xl p-4 hover:border-accent-500/50 transition-colors cursor-pointer"
              onClick={() => setSelectedMatter(matter)}
            >
              <div className="flex items-start gap-4">
                {/* Icon */}
                <div className="w-12 h-12 rounded-xl bg-accent-500/20 flex items-center justify-center">
                  <Briefcase className="w-6 h-6 text-accent-400" />
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-sm font-mono text-accent-400">{matter.number}</span>
                    <h3 className="font-semibold text-white">{matter.name}</h3>
                    <span className={`px-2 py-0.5 rounded text-xs capitalize ${getStatusColor(matter.status)}`}>
                      {STATUS_OPTIONS.find(s => s.value === matter.status)?.label}
                    </span>
                    {matter.priority !== 'normal' && (
                      <span className={`flex items-center gap-1 text-xs ${getPriorityColor(matter.priority)}`}>
                        <AlertCircle className="w-3 h-3" />
                        {matter.priority}
                      </span>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-4 mt-2 text-sm text-primary-400 flex-wrap">
                    <span className="flex items-center gap-1">
                      {matter.clientType === 'entity' ? (
                        <Building className="w-4 h-4" />
                      ) : (
                        <User className="w-4 h-4" />
                      )}
                      {matter.clientName}
                    </span>
                    <span className="flex items-center gap-1">
                      <Tag className="w-4 h-4" />
                      {getTypeLabel(matter.type)}
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      Opened {formatDate(matter.openedDate)}
                    </span>
                  </div>

                  {matter.property && (
                    <div className="flex items-center gap-1 mt-2 text-sm text-primary-500">
                      <MapPin className="w-4 h-4" />
                      {matter.property.address}, {matter.property.city} ({matter.property.county} County)
                    </div>
                  )}
                </div>

                {/* Stats */}
                <div className="text-center">
                  <p className="text-primary-400 text-sm">Documents</p>
                  <p className="text-lg font-semibold text-white">{matter.documentCount}</p>
                </div>

                {/* Arrow */}
                <ChevronRight className="w-5 h-5 text-primary-500" />
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Matter Detail Modal */}
      <AnimatePresence>
        {selectedMatter && (
          <MatterDetailModal
            matter={selectedMatter}
            onClose={() => setSelectedMatter(null)}
          />
        )}
      </AnimatePresence>

      {/* New Matter Modal */}
      <AnimatePresence>
        {showNewMatterModal && (
          <NewMatterModal
            onClose={() => setShowNewMatterModal(false)}
            onCreated={(matter) => {
              setMatters([matter, ...matters]);
              setShowNewMatterModal(false);
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// Matter Detail Modal
function MatterDetailModal({ matter, onClose }: { matter: Matter; onClose: () => void }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="w-full max-w-3xl bg-primary-900 rounded-xl border border-primary-700 overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-primary-700">
          <div>
            <div className="flex items-center gap-3">
              <span className="text-sm font-mono text-accent-400">{matter.number}</span>
              <h2 className="text-xl font-semibold text-white">{matter.name}</h2>
            </div>
            <p className="text-sm text-primary-400 mt-1">{matter.clientName}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg">
            <X className="w-5 h-5 text-primary-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Status Bar */}
          <div className="flex items-center gap-4">
            <div className="flex-1 bg-primary-800 rounded-lg p-4">
              <p className="text-sm text-primary-400">Status</p>
              <p className="text-lg font-semibold text-white capitalize">
                {STATUS_OPTIONS.find(s => s.value === matter.status)?.label}
              </p>
            </div>
            <div className="flex-1 bg-primary-800 rounded-lg p-4">
              <p className="text-sm text-primary-400">Type</p>
              <p className="text-lg font-semibold text-white">
                {MATTER_TYPES.find(t => t.value === matter.type)?.label}
              </p>
            </div>
            <div className="flex-1 bg-primary-800 rounded-lg p-4">
              <p className="text-sm text-primary-400">Documents</p>
              <p className="text-lg font-semibold text-white">{matter.documentCount}</p>
            </div>
          </div>

          {/* Property Info */}
          {matter.property && (
            <div>
              <h3 className="text-sm font-medium text-primary-400 mb-3">Property</h3>
              <div className="bg-primary-800/50 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-accent-400 mt-0.5" />
                  <div>
                    <p className="text-white">{matter.property.address}</p>
                    <p className="text-primary-400">{matter.property.city}, {matter.property.county} County, WI</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Timeline */}
          <div>
            <h3 className="text-sm font-medium text-primary-400 mb-3">Timeline</h3>
            <div className="space-y-3">
              <div className="flex items-center gap-3 text-sm">
                <div className="w-2 h-2 rounded-full bg-green-400" />
                <span className="text-primary-300">Matter opened</span>
                <span className="text-primary-500 ml-auto">
                  {matter.openedDate.toLocaleDateString()}
                </span>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <div className="w-2 h-2 rounded-full bg-blue-400" />
                <span className="text-primary-300">Documents generated</span>
                <span className="text-primary-500 ml-auto">Oct 20, 2024</span>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <div className="w-2 h-2 rounded-full bg-yellow-400" />
                <span className="text-primary-300">Sent for signature</span>
                <span className="text-primary-500 ml-auto">Oct 25, 2024</span>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 px-6 py-4 bg-primary-800/30 border-t border-primary-700">
          <button className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-accent-500 text-white rounded-lg hover:bg-accent-600 transition-colors">
            <FileText className="w-4 h-4" />
            Generate Document
          </button>
          <button className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-primary-700 text-white rounded-lg hover:bg-primary-600 transition-colors">
            <Clock className="w-4 h-4" />
            View Activity
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// New Matter Modal
function NewMatterModal({ onClose, onCreated }: { onClose: () => void; onCreated: (matter: Matter) => void }) {
  const [name, setName] = useState('');
  const [type, setType] = useState('');
  const [clientName, setClientName] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    await new Promise(resolve => setTimeout(resolve, 500));

    const newMatter: Matter = {
      id: `new-${Date.now()}`,
      number: `2024-${String(Math.floor(Math.random() * 1000)).padStart(4, '0')}-${type.slice(0, 3).toUpperCase()}`,
      name,
      clientId: 'new-client',
      clientName,
      clientType: 'individual',
      type,
      status: 'intake',
      priority: 'normal',
      responsibleAttorney: 'Sarah Johnson',
      openedDate: new Date(),
      documentCount: 0,
    };

    onCreated(newMatter);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="w-full max-w-md bg-primary-900 rounded-xl border border-primary-700 overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-primary-700">
          <h2 className="text-xl font-semibold text-white">New Matter</h2>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg">
            <X className="w-5 h-5 text-primary-400" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm text-primary-400 mb-1">Matter Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Smith Property Purchase"
              className="w-full px-3 py-2 bg-primary-800 border border-primary-600 text-white rounded-lg"
              required
            />
          </div>

          <div>
            <label className="block text-sm text-primary-400 mb-1">Matter Type</label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="w-full px-3 py-2 bg-primary-800 border border-primary-600 text-white rounded-lg"
              required
            >
              <option value="">Select type...</option>
              {MATTER_TYPES.map(t => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm text-primary-400 mb-1">Client</label>
            <input
              type="text"
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              placeholder="Search or enter client name"
              className="w-full px-3 py-2 bg-primary-800 border border-primary-600 text-white rounded-lg"
              required
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-primary-700 text-white rounded-lg hover:bg-primary-600 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-accent-500 text-white rounded-lg hover:bg-accent-600 transition-colors disabled:opacity-50"
            >
              {saving ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Check className="w-5 h-5" />
                  Create Matter
                </>
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}
